import { apiClient } from '@/modules/shared/services/apiClient';
import { ApiResponse } from '@/modules/shared/types';
import {
  CallLogAnalyticsDto,
  CallLogDto,
  EntityMiniAnalyticsDto,
  CallLogQueryParams,
  CallLogTypeDto,
  CreateCallLogTypeDto,
  CreateCallLogDto,
  SmartFollowUpDto,
  CallLogFollowUpActionCenterSummaryDto,
  UpdateCallLogTypeDto,
  UpdateCallLogDto,
  PaginatedCallLogResult,
} from '../types/callLog';

const BASE = (programId: string) => `/programs/${programId}/call-logs`;
const TYPES_BASE = (programId: string) => `/programs/${programId}/call-log-types`;
const FOLLOW_UPS_BASE = (programId: string) => `/programs/${programId}/follow-ups`;

export const callLogService = {
  // ─── READ ──────────────────────────────────────────

  async getByProgram(
    programId: string,
    params?: CallLogQueryParams
  ): Promise<ApiResponse<PaginatedCallLogResult>> {
    const res = await apiClient.get<ApiResponse<PaginatedCallLogResult>>(BASE(programId), {
      params,
    });
    return res.data;
  },

  async getById(programId: string, callLogId: string): Promise<ApiResponse<CallLogDto>> {
    const res = await apiClient.get<ApiResponse<CallLogDto>>(`${BASE(programId)}/${callLogId}`);
    return res.data;
  },

  async getByEntity(
    programId: string,
    entityType: string,
    entityId: string
  ): Promise<ApiResponse<CallLogDto[]>> {
    const res = await apiClient.get<ApiResponse<CallLogDto[]>>(
      `${BASE(programId)}/by-entity/${entityType}/${entityId}`
    );
    return res.data;
  },

  async getAnalytics(
    programId: string,
    params?: Omit<CallLogQueryParams, 'page' | 'pageSize' | 'callStatus'> & { callStatus?: string }
  ): Promise<ApiResponse<CallLogAnalyticsDto>> {
    const res = await apiClient.get<ApiResponse<CallLogAnalyticsDto>>(`${BASE(programId)}/analytics`, {
      params,
    });
    return res.data;
  },

  async getFollowUps(
    programId: string,
    params?: Omit<CallLogQueryParams, 'page' | 'pageSize'> & {
      status?: 'due' | 'overdue' | 'upcoming';
      assignedTo?: string;
      priority?: 'low' | 'medium' | 'high' | 'critical';
    }
  ): Promise<ApiResponse<SmartFollowUpDto>> {
    const res = await apiClient.get<ApiResponse<SmartFollowUpDto>>(`${BASE(programId)}/follow-ups`, {
      params,
    });
    return res.data;
  },

  async getFollowUpActionCenterSummary(
    programId: string,
    queueSize = 10,
  ): Promise<ApiResponse<CallLogFollowUpActionCenterSummaryDto>> {
    const res = await apiClient.get<ApiResponse<CallLogFollowUpActionCenterSummaryDto>>(
      `${FOLLOW_UPS_BASE(programId)}/action-center-summary`,
      { params: { queueSize } },
    );
    return res.data;
  },

  async getEntityMiniAnalytics(programId: string, entityType: string, entityId: string): Promise<ApiResponse<EntityMiniAnalyticsDto>> {
    const res = await apiClient.get<ApiResponse<EntityMiniAnalyticsDto>>(`${BASE(programId)}/analytics/entity/${entityType}/${entityId}`);
    return res.data;
  },

  async completeFollowUpById(programId: string, followUpId: string, notes?: string): Promise<ApiResponse<unknown>> {
    const res = await apiClient.post<ApiResponse<unknown>>(`${FOLLOW_UPS_BASE(programId)}/${followUpId}/complete`, {
      notes: notes ?? null,
    });
    return res.data;
  },

  async snoozeFollowUpById(programId: string, followUpId: string, snoozeUntilUtc: string, notes?: string): Promise<ApiResponse<unknown>> {
    const res = await apiClient.post<ApiResponse<unknown>>(`${FOLLOW_UPS_BASE(programId)}/${followUpId}/snooze`, {
      snoozeUntilUtc,
      notes: notes ?? null,
    });
    return res.data;
  },

  async getTypes(programId: string, includeInactive = false): Promise<ApiResponse<CallLogTypeDto[]>> {
    const res = await apiClient.get<ApiResponse<CallLogTypeDto[]>>(TYPES_BASE(programId), {
      params: { includeInactive },
    });
    return res.data;
  },

  async createType(programId: string, dto: CreateCallLogTypeDto): Promise<ApiResponse<CallLogTypeDto>> {
    const res = await apiClient.post<ApiResponse<CallLogTypeDto>>(TYPES_BASE(programId), dto);
    return res.data;
  },

  async updateType(programId: string, typeId: string, dto: UpdateCallLogTypeDto): Promise<ApiResponse<CallLogTypeDto>> {
    const res = await apiClient.put<ApiResponse<CallLogTypeDto>>(`${TYPES_BASE(programId)}/${typeId}`, dto);
    return res.data;
  },

  // ─── CREATE / UPDATE / DELETE ─────────────────────

  async create(programId: string, dto: CreateCallLogDto): Promise<ApiResponse<CallLogDto>> {
    const res = await apiClient.post<ApiResponse<CallLogDto>>(BASE(programId), dto);
    return res.data;
  },

  async update(
    programId: string,
    callLogId: string,
    dto: UpdateCallLogDto
  ): Promise<ApiResponse<CallLogDto>> {
    const res = await apiClient.put<ApiResponse<CallLogDto>>(
      `${BASE(programId)}/${callLogId}`,
      dto
    );
    return res.data;
  },

  async remove(programId: string, callLogId: string): Promise<ApiResponse<boolean>> {
    const res = await apiClient.delete<ApiResponse<boolean>>(`${BASE(programId)}/${callLogId}`);
    return res.data;
  },
};
